import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import "./ViewBalanceTable.css";

/* helpers */
const groupBy = (arr, key) =>
    arr.reduce((acc, item) => {
        const val = item[key] || "Unknown";
        if (!acc[val]) acc[val] = [];
        acc[val].push(item);
        return acc;
    }, {});

const sum = (arr, key) =>
    arr.reduce((s, i) => s + (i[key] || 0), 0);

const format = (val) =>
    Number(val || 0).toLocaleString("en-IN");

const ViewClosingBalance = () => {
    const location = useLocation();
    const { id, name, jfmcTypeId } = location.state || {};

    const [data, setData] = useState([]);

    const [openCircle, setOpenCircle] = useState({});
    const [openDivision, setOpenDivision] = useState({});
    const [openRange, setOpenRange] = useState({});

    const Api = process.env.REACT_APP_API;

    useEffect(() => {
        if (id) fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        const res = await axios.post(
            `${Api}/api/closing-balance/details`,
            {
                id,
                jfmcTypeId
            },
            Authorization()
        );

        setData(res.data.data || []);
    };

    const circleGroups = groupBy(data, "circle");

    /* Expand / Collapse */
    const expandAll = () => {
        const c = {}, d = {}, r = {};
        Object.entries(circleGroups).forEach(([circle, cData]) => {
            c[circle] = true;

            const divs = groupBy(cData, "division");
            Object.entries(divs).forEach(([division, dData]) => {
                const divKey = `${circle}-${division}`;
                d[divKey] = true;

                const ranges = groupBy(dData, "range");
                Object.keys(ranges).forEach((range) => {
                    r[`${divKey}-${range}`] = true;
                });
            });
        });

        setOpenCircle(c);
        setOpenDivision(d);
        setOpenRange(r);
    };

    const collapseAll = () => {
        setOpenCircle({});
        setOpenDivision({});
        setOpenRange({});
    };

    return (
        <div className="rwr-container">

            {/* HEADER */}
            <div className="rwr-page-header">
                <h5 className="rwr-title">
                    <i className="mdi mdi-bank"></i> {name} - Details
                </h5>

                <div className="rwr-header-actions">
                    <button className="rwr-btn" onClick={expandAll}>
                        <i className="mdi mdi-unfold-more-horizontal"></i> Expand All
                    </button>

                    <button className="rwr-btn" onClick={collapseAll}>
                        <i className="mdi mdi-unfold-less-horizontal"></i> Collapse All
                    </button>
                </div>
            </div>

            {/* CIRCLES */}
            {Object.entries(circleGroups).map(([circle, circleData]) => {
                const isCircleOpen = openCircle[circle];
                const divisionGroups = groupBy(circleData, "division");

                return (
                    <div key={circle} className="rwr-circle-block">

                        {/* CIRCLE HEADER */}
                        <div
                            className="rwr-circle-header"
                            onClick={() =>
                                setOpenCircle(p => ({ ...p, [circle]: !p[circle] }))
                            }
                        >
                            <div className="rwr-row-left">
                                <span className="rwr-level-badge rwr-badge-circle">Circle</span>
                                <span className="rwr-row-name">{circle}</span>
                            </div>

                            <div className="rwr-row-mid">
                                <div className="rwr-stats-row">
                                    <div className="rwr-stat-chip">
                                        <span>Cash</span>
                                        <b>₹{format(sum(circleData, "totalCash")).toLocaleString("en-IN")}</b>
                                    </div>
                                    <div className="rwr-stat-chip">
                                        <span>Bank</span>
                                        <b>₹{format(sum(circleData, "totalBank")).toLocaleString("en-IN")}</b>
                                    </div>
                                    <div className="rwr-stat-chip">
                                        <span>Total</span>
                                        <b>₹{format(sum(circleData, "total")).toLocaleString("en-IN")}</b>
                                    </div>
                                </div>
                            </div>

                            <div className="rwr-row-right">
                                <span className="rwr-count-pill">
                                    {Object.keys(divisionGroups).length} Div
                                </span>
                                <i className={`mdi mdi-chevron-down rwr-chevron ${isCircleOpen ? "rotated" : ""}`} />
                            </div>
                        </div>

                        {/* DIVISIONS */}
                        {isCircleOpen && (
                            <div className="rwr-children">
                                {Object.entries(divisionGroups).map(([division, divisionData]) => {
                                    const divKey = `${circle}-${division}`;
                                    const isDivOpen = openDivision[divKey];
                                    const rangeGroups = groupBy(divisionData, "range");

                                    return (
                                        <div key={division} className="rwr-division-block">

                                            {/* DIVISION HEADER */}
                                            <div
                                                className="rwr-division-header"
                                                onClick={() =>
                                                    setOpenDivision(p => ({
                                                        ...p,
                                                        [divKey]: !p[divKey],
                                                    }))
                                                }
                                            >
                                                <div className="rwr-row-left">
                                                    <span className="rwr-level-badge rwr-badge-division">
                                                        Division
                                                    </span>
                                                    <span className="rwr-row-name">{division}</span>
                                                </div>

                                                <div className="rwr-row-mid">
                                                    <div className="rwr-stats-row">
                                                        <div className="rwr-stat-chip">
                                                            <span> Cash </span>
                                                            <p>₹{sum(divisionData, "totalCash").toLocaleString("en-IN")}</p>
                                                        </div>
                                                        <div className="rwr-stat-chip">
                                                            <span> Bank </span>
                                                            <p>₹{sum(divisionData, "totalBank").toLocaleString("en-IN")}</p>
                                                        </div>
                                                        <div className="rwr-stat-chip">
                                                            <span> Total </span>
                                                            <p>₹{sum(divisionData, "total").toLocaleString("en-IN")}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="rwr-row-right">
                                                    <span className="rwr-count-pill">
                                                        {Object.keys(rangeGroups).length} Ranges
                                                    </span>
                                                    <i className={`mdi mdi-chevron-down rwr-chevron ${isDivOpen ? "rotated" : ""}`} />
                                                </div>
                                            </div>

                                            {/* RANGES */}
                                            {isDivOpen && (
                                                <div className="rwr-children">
                                                    {Object.entries(rangeGroups).map(([range, rangeData]) => {
                                                        const rangeKey = `${divKey}-${range}`;
                                                        const isRangeOpen = openRange[rangeKey];

                                                        return (
                                                            <div key={range} className="rwr-range-block">

                                                                {/* RANGE HEADER */}
                                                                <div
                                                                    className="rwr-range-header"
                                                                    onClick={() =>
                                                                        setOpenRange(p => ({
                                                                            ...p,
                                                                            [rangeKey]: !p[rangeKey],
                                                                        }))
                                                                    }
                                                                >
                                                                    <div className="rwr-row-left">
                                                                        <span className="rwr-level-badge rwr-badge-range">
                                                                            Range
                                                                        </span>
                                                                        <span className="rwr-row-name">{range}</span>
                                                                    </div>

                                                                    <div className="rwr-row-mid">
                                                                        <div className="rwr-stats-row">
                                                                            <div className="rwr-stat-chip">
                                                                                <span>Cash </span>
                                                                                <p>₹{sum(rangeData, "totalCash").toLocaleString("en-IN")}</p>

                                                                            </div>
                                                                            <div className="rwr-stat-chip">
                                                                                <span>Bank </span>
                                                                                <p>₹{sum(rangeData, "totalBank").toLocaleString("en-IN")}</p>
                                                                            </div>
                                                                            <div className="rwr-stat-chip">
                                                                                <span>Total </span>
                                                                                <p>₹{sum(rangeData, "total").toLocaleString("en-IN")}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>


                                                                    <div className="rwr-row-right">
                                                                        <span className="rwr-count-pill rwr-pill-green">
                                                                            {rangeData.length} VFCs
                                                                        </span>
                                                                        <i className={`mdi mdi-chevron-down rwr-chevron ${isRangeOpen ? "rotated" : ""}`} />
                                                                    </div>
                                                                </div>

                                                                {/* VFC TABLE */}
                                                                {isRangeOpen && (
                                                                    <div className="cbt-table-wrapper">
                                                                        <table className="cbt-table">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>#</th>
                                                                                    <th>VFC</th>
                                                                                    <th>Cash</th>
                                                                                    <th>Bank</th>
                                                                                    <th>Total</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {rangeData.map((vfc, index) => (
                                                                                    <tr key={index}>
                                                                                        <td>{index + 1}</td>
                                                                                        <td>{vfc.vfc}</td>
                                                                                        <td>₹{format(vfc.totalCash).toLocaleString("en-IN")}</td>
                                                                                        <td>₹{format(vfc.totalBank).toLocaleString("en-IN")}</td>
                                                                                        <td><b>₹{format(vfc.total).toLocaleString("en-IN")}</b></td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                )}

                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ViewClosingBalance;